import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { goldGradient } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';

interface ErrorFallbackProps {
	error: Error;
	resetError: () => void;
}

export const AppErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
	const isDev = import.meta.env.DEV;

	return (
		<div className="min-h-screen-safe flex items-center justify-center bg-[radial-gradient(ellipse_at_top,hsl(220,10%,12%),hsl(220,10%,6%)_50%,hsl(220,10%,4%))]">
			<div className="text-center px-6 max-w-md">
				<div className="mb-6">
					<AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
					<h1 className="text-2xl font-logo font-bold text-white mb-2">
						Oops! Something went wrong
					</h1>
					<p className="text-muted-foreground mb-4">
						We encountered an unexpected error. Please try reloading the page.
					</p>

					{isDev && (
						<div className="bg-black/40 border border-destructive/20 rounded-lg p-4 mb-4 text-left">
							<p className="text-xs font-mono text-destructive break-all">{error.message}</p>
						</div>
					)}
				</div>

				<div className="flex flex-col gap-3">
					<Button 
						onClick={() => window.location.reload()} 
						className={cn(goldGradient, "w-full text-black hover:opacity-90")}
					>
						Reload Page
					</Button>

					<Button 
						onClick={resetError} 
						variant="outline" 
						className="w-full hover:border-white/20"
					>
						Try Again
					</Button>

					<a href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
						Go to Homepage
					</a>
				</div>
			</div>
		</div>
	);
};

export const ComponentErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
	const isDev = import.meta.env.DEV;

	return (
		<div className="flex items-center justify-center p-8 bg-black/20 border border-destructive/20 rounded-lg">
			<div className="text-center max-w-md">
				<AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
				<h3 className="text-lg font-semibold text-white mb-2">Failed to load component</h3>
				<p className="text-sm text-muted-foreground mb-4">This section encountered an error.</p>

				{isDev && (
					<div className="bg-black/60 border border-destructive/20 rounded p-3 mb-4 text-left">
						<p className="text-xs font-mono text-destructive break-all">{error.message}</p>
					</div>
				)}

				<Button onClick={resetError} variant="outline" size="sm">
					Try Again
				</Button>
			</div>
		</div>
	);
};
